import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Badge,
  Rating,
  Divider,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  ArrowBack,
  Assessment,
  Person,
  Timer,
  CalendarToday,
  CheckCircle,
  Warning,
  HourglassEmpty,
  Grade,
  Visibility,
  Save,
  Close,
  FileDownload,
  Link as LinkIcon,
  Code,
  Description,
  VideoCall,
  Schedule
} from '@mui/icons-material';
import axios from 'axios';

const TaskSubmissions = () => {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const applicationId = queryParams.get('applicationId');
  const candidateId = queryParams.get('candidateId');
  
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [scoreDialog, setScoreDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [interviews, setInterviews] = useState([]);
  const [pageTitle, setPageTitle] = useState('Task Submissions');
  
  const [scoreData, setScoreData] = useState({
    score: 0,
    feedback: '',
    status: 'reviewed'
  });

  const [interviewData, setInterviewData] = useState({
    meetingLink: '',
    scheduledDate: '',
    duration: 60,
    interviewers: '',
    interviewType: 'technical',
    notes: '',
    instructions: ''
  });

  useEffect(() => {
    if (taskId) {
      fetchTaskAndSubmissions();
    } else if (applicationId && candidateId) {
      fetchCandidateSubmissions();
    }
    fetchInterviews();
  }, [taskId, applicationId, candidateId]);

  const fetchTaskAndSubmissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch task details
      const taskResponse = await axios.get(
        `http://localhost:5000/api/tasks/${taskId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setTask(taskResponse.data.data);
      setPageTitle(`Task Submissions - ${taskResponse.data.data.title}`);
      
      // Fetch submissions
      const submissionsResponse = await axios.get(
        `http://localhost:5000/api/tasks/${taskId}/submissions`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSubmissions(submissionsResponse.data.data || []);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidateSubmissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch submissions for a specific candidate
      const submissionsResponse = await axios.get(
        `http://localhost:5000/api/tasks/submissions/candidate/${candidateId}?applicationId=${applicationId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const data = submissionsResponse.data.data || [];
      setSubmissions(data);
      
      // If submissions are found, fetch the task details of the first submission
      if (data.length > 0) {
        const firstSubmission = data[0];
        if (firstSubmission.task?._id) {
          const taskResponse = await axios.get(
            `http://localhost:5000/api/tasks/${firstSubmission.task._id}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          setTask(taskResponse.data.data);
        }
        
        // Set page title
        if (firstSubmission.candidate) {
          const candidateName = firstSubmission.candidate.profile?.firstName || firstSubmission.candidate.email;
          setPageTitle(`Submissions - ${candidateName}`);
        }
      }
      
    } catch (err) {
      console.error('Error fetching candidate submissions:', err);
      setError('Failed to load candidate submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchInterviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `http://localhost:5000/api/interviews`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setInterviews(response.data.data || []);
    } catch (err) {
      console.error('Error fetching interviews:', err);
    }
  };

  const handleScoreSubmission = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Determine pass/fail based on score
      const isPassed = scoreData.score >= (task?.passingScore || 60);
      const submissionStatus = isPassed ? 'passed' : 'failed';
      
      console.log('Submitting score:', {
        score: scoreData.score,
        feedback: scoreData.feedback,
        status: submissionStatus,
        submissionId: selectedSubmission._id
      });
      
      const response = await axios.post(
        `http://localhost:5000/api/tasks/submissions/${selectedSubmission._id}/score`,
        {
          score: scoreData.score,
          feedback: scoreData.feedback,
          status: submissionStatus
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Score submission response:', response.data);
      
      setSuccess('Score saved successfully');
      setScoreDialog(false);
      
      // Refresh the submissions list
      if (taskId) {
        fetchTaskAndSubmissions();
      } else if (applicationId && candidateId) {
        fetchCandidateSubmissions();
      }
      
      // Reset score data
      setScoreData({
        score: 0,
        feedback: '',
        status: 'reviewed'
      });
      
    } catch (err) {
      console.error('Error scoring submission:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to save score');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'reviewed': 'info',
      'passed': 'success',
      'failed': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': <HourglassEmpty />,
      'reviewed': <Assessment />,
      'passed': <CheckCircle />,
      'failed': <Warning />
    };
    return icons[status] || <Assessment />;
  };

  // Handle both 'pending' and 'submitted' as pending status
  const pendingSubmissions = submissions.filter(s => s.status === 'pending' || s.status === 'submitted');
  const reviewedSubmissions = submissions.filter(s => s.status === 'reviewed');
  const passedSubmissions = submissions.filter(s => s.status === 'passed');
  const failedSubmissions = submissions.filter(s => s.status === 'failed');

  const getFilteredSubmissions = () => {
    switch (tabValue) {
      case 0: return submissions; // All submissions
      case 1: return pendingSubmissions;
      case 2: return reviewedSubmissions;
      case 3: return passedSubmissions;
      case 4: return failedSubmissions;
      default: return submissions;
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate(applicationId ? '/manage-applications' : '/tasks')} 
          sx={{ mr: 2 }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4">
            {pageTitle}
          </Typography>
          {task && (
            <Typography variant="subtitle1" color="text.secondary">
              {task.title}
            </Typography>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Task Summary */}
      {task && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary">
                  {submissions.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Submissions
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="warning.main">
                  {pendingSubmissions.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Review
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="success.main">
                  {passedSubmissions.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Passed
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="error.main">
                  {failedSubmissions.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab 
            label={
              <Badge badgeContent={submissions.length} color="primary">
                All
              </Badge>
            }
          />
          <Tab 
            label={
              <Badge badgeContent={pendingSubmissions.length} color="warning">
                Pending
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={reviewedSubmissions.length} color="info">
                Reviewed
              </Badge>
            }
          />
          <Tab 
            label={
              <Badge badgeContent={passedSubmissions.length} color="success">
                Passed
              </Badge>
            }
          />
          <Tab 
            label={
              <Badge badgeContent={failedSubmissions.length} color="error">
                Failed
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {/* Submissions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Candidate</TableCell>
              <TableCell>Submitted At</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredSubmissions().map((submission) => (
              <TableRow key={submission._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2 }}>
                      {submission.candidate?.profile?.firstName?.[0] || 
                       submission.candidate?.email?.[0]?.toUpperCase() || 
                       <Person />}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">
                        {submission.candidate?.profile?.firstName && submission.candidate?.profile?.lastName
                          ? `${submission.candidate.profile.firstName} ${submission.candidate.profile.lastName}`
                          : submission.candidate?.email || 'Anonymous'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {submission.candidate?.email || 'N/A'}
                      </Typography>
                      {submission.task && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Task: {submission.task.title}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(submission.submittedAt)}
                  </Typography>
                  {submission.isLateSubmission && (
                    <Chip
                      label="Late"
                      color="warning"
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {submission.score !== null && submission.score !== undefined ? (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {submission.score}/{task?.maxScore || 100}
                        </Typography>
                        {submission.evaluatedAt && (
                          <Chip
                            label="Scored"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        ({((submission.score / (task?.maxScore || 100)) * 100).toFixed(0)}%)
                      </Typography>
                      {submission.evaluatedAt && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Evaluated: {formatDate(submission.evaluatedAt)}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Not scored
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(submission.status)}
                    label={submission.status}
                    color={getStatusColor(submission.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Submission">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setViewDialog(true);
                      }}
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={submission.score !== null && submission.score !== undefined ? "Edit Score" : "Score Submission"}>
                    <IconButton
                      size="small"
                      color={submission.score !== null && submission.score !== undefined ? "primary" : "default"}
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setScoreData({
                          score: submission.score || 0,
                          feedback: submission.feedback || '',
                          status: submission.status || 'reviewed'
                        });
                        setScoreDialog(true);
                      }}
                    >
                      <Grade />
                    </IconButton>
                  </Tooltip>
                  {submission.status === 'passed' && (
                    <Tooltip title="Schedule Interview">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setSelectedSubmission(submission);
                          const now = new Date();
                          now.setDate(now.getDate() + 2);
                          setInterviewData({
                            ...interviewData,
                            scheduledDate: now.toISOString().slice(0, 16)
                          });
                          setScheduleDialog(true);
                        }}
                      >
                        <VideoCall />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {getFilteredSubmissions().length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No submissions found
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* View Submission Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            View Submission
            <IconButton onClick={() => setViewDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSubmission && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Candidate Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body1">
                    {selectedSubmission.candidate?.profile?.firstName && selectedSubmission.candidate?.profile?.lastName
                      ? `${selectedSubmission.candidate.profile.firstName} ${selectedSubmission.candidate.profile.lastName}`
                      : selectedSubmission.candidate?.email || 'Anonymous'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body1">
                    {selectedSubmission.candidate?.email || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Submitted At</Typography>
                  <Typography variant="body1">
                    {formatDate(selectedSubmission.submittedAt)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Chip
                    label={selectedSubmission.status}
                    color={getStatusColor(selectedSubmission.status)}
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Submission Content
              </Typography>
              
              {/* Display main content */}
              {selectedSubmission.content && (
                <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                  <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedSubmission.content}
                  </Typography>
                </Paper>
              )}

              {/* Display answers based on type */}
              {selectedSubmission.answers && (
                <Box sx={{ mb: 2 }}>
                  {/* Essay answers */}
                  {selectedSubmission.answers.essay && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Essay Response:</Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                          {selectedSubmission.answers.essay}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {/* Multiple choice answers */}
                  {selectedSubmission.answers.multipleChoice && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Multiple Choice Answers:</Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <pre>{JSON.stringify(selectedSubmission.answers.multipleChoice, null, 2)}</pre>
                      </Paper>
                    </Box>
                  )}

                  {/* Code submission */}
                  {selectedSubmission.answers.code && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Code Solution:</Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.900' }}>
                        <Typography 
                          variant="body2" 
                          component="pre" 
                          sx={{ 
                            color: 'grey.100',
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          {selectedSubmission.answers.code.solution || 'No code solution provided'}
                        </Typography>
                      </Paper>
                      
                      {/* Test Results */}
                      {selectedSubmission.answers.code.testResults && selectedSubmission.answers.code.testResults.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>Test Results:</Typography>
                          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                            {selectedSubmission.answers.code.testResults.map((test, index) => (
                              <Box key={index} sx={{ mb: 1, p: 1, border: '1px solid', borderColor: test.passed ? 'success.main' : 'error.main', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  Test Case {test.testCaseIndex + 1}: {test.passed ? 'PASSED' : 'FAILED'}
                                </Typography>
                                {test.output && (
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                    Output: {test.output}
                                  </Typography>
                                )}
                                {test.error && (
                                  <Typography variant="body2" color="error" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                    Error: {test.error}
                                  </Typography>
                                )}
                                {test.executionTime && (
                                  <Typography variant="caption" color="text.secondary">
                                    Execution Time: {test.executionTime}ms
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Paper>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* URL submissions */}
                  {selectedSubmission.answers.urls && selectedSubmission.answers.urls.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Submitted URLs:</Typography>
                      {selectedSubmission.answers.urls.map((urlItem, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Typography variant="body1">
                            <LinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            <a href={urlItem.url} target="_blank" rel="noopener noreferrer">
                              {urlItem.url}
                            </a>
                            {urlItem.type && (
                              <Chip 
                                label={urlItem.type} 
                                size="small" 
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* File uploads */}
                  {selectedSubmission.answers.files && selectedSubmission.answers.files.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Uploaded Files:</Typography>
                      {selectedSubmission.answers.files.map((file, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Button
                            variant="outlined"
                            startIcon={<FileDownload />}
                            href={`http://localhost:5000/${file.path}`}
                            target="_blank"
                            sx={{ mr: 1 }}
                          >
                            {file.originalName} ({(file.size / 1024).toFixed(2)} KB)
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Time spent */}
              {selectedSubmission.timeSpent !== undefined && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">Time Spent:</Typography>
                  <Typography variant="body1">
                    {Math.floor(selectedSubmission.timeSpent / 60)} minutes {selectedSubmission.timeSpent % 60} seconds
                  </Typography>
                </Box>
              )}

              {selectedSubmission.score !== null && selectedSubmission.score !== undefined && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Evaluation
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Score</Typography>
                      <Typography variant="h4">
                        {selectedSubmission.score}/{task?.maxScore || 100}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Feedback</Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                        <Typography variant="body1">
                          {selectedSubmission.feedback || 'No feedback provided'}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Score Submission Dialog */}
      <Dialog open={scoreDialog} onClose={() => setScoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {selectedSubmission?.score !== undefined && selectedSubmission?.score !== null 
                ? 'Edit Score' 
                : 'Score Submission'
              }
            </Typography>
            {selectedSubmission?.evaluatedAt && (
              <Chip 
                label={`Last evaluated: ${formatDate(selectedSubmission.evaluatedAt)}`}
                size="small"
                color="info"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedSubmission?.score !== undefined && selectedSubmission?.score !== null && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This submission has already been scored. You can modify the score and feedback below.
                </Typography>
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Score"
              type="number"
              value={scoreData.score || ''}
              onChange={(e) => setScoreData({ ...scoreData, score: parseInt(e.target.value) || 0 })}
              InputProps={{
                inputProps: { min: 0, max: task?.maxScore || 100 }
              }}
              helperText={`Maximum score: ${task?.maxScore || 100} | Passing score: ${task?.passingScore || 60}`}
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Feedback"
              value={scoreData.feedback}
              onChange={(e) => setScoreData({ ...scoreData, feedback: e.target.value })}
              placeholder="Provide feedback for the candidate..."
              sx={{ mb: 2 }}
            />
            
            {scoreData.score >= (task?.passingScore || 60) ? (
              <Alert severity="success">
                This score will mark the submission as PASSED
              </Alert>
            ) : (
              <Alert severity="error">
                This score will mark the submission as FAILED
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleScoreSubmission} 
            variant="contained" 
            startIcon={<Save />}
          >
            {selectedSubmission?.score !== undefined && selectedSubmission?.score !== null 
              ? 'Update Score' 
              : 'Save Score'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Interview Dialog */}
      <Dialog open={scheduleDialog} onClose={() => setScheduleDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Schedule sx={{ mr: 1 }} />
            Schedule Interview
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSubmission && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  1. Open Google Meet to create a meeting link
                  <Button
                    size="small"
                    startIcon={<VideoCall />}
                    onClick={() => window.open('https://meet.google.com/', '_blank')}
                    sx={{ ml: 2 }}
                  >
                    Open Google Meet
                  </Button>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  2. Copy the meeting link and paste it below
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Candidate: {selectedSubmission.candidate?.name || 'Anonymous'} ({selectedSubmission.candidate?.email})
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Google Meet Link"
                    value={interviewData.meetingLink}
                    onChange={(e) => setInterviewData({ ...interviewData, meetingLink: e.target.value })}
                    placeholder="https://meet.google.com/xxx-xxxx-xxx"
                    required
                    helperText="Paste the Google Meet link here"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Interview Date & Time"
                    type="datetime-local"
                    value={interviewData.scheduledDate}
                    onChange={(e) => setInterviewData({ ...interviewData, scheduledDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Duration"
                    value={interviewData.duration}
                    onChange={(e) => setInterviewData({ ...interviewData, duration: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1.5 hours</option>
                    <option value={120}>2 hours</option>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Interviewers"
                    value={interviewData.interviewers}
                    onChange={(e) => setInterviewData({ ...interviewData, interviewers: e.target.value })}
                    placeholder="Enter interviewer names (comma separated)"
                    helperText="e.g., John Doe, Jane Smith"
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Interview Type"
                    value={interviewData.interviewType}
                    onChange={(e) => setInterviewData({ ...interviewData, interviewType: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="technical">Technical</option>
                    <option value="hr">HR</option>
                    <option value="behavioral">Behavioral</option>
                    <option value="cultural">Cultural Fit</option>
                    <option value="final">Final Round</option>
                    <option value="screening">Screening</option>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Instructions for Candidate"
                    value={interviewData.instructions}
                    onChange={(e) => setInterviewData({ ...interviewData, instructions: e.target.value })}
                    placeholder="e.g., Please join 5 minutes early. Have your resume ready."
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Internal Notes"
                    value={interviewData.notes}
                    onChange={(e) => setInterviewData({ ...interviewData, notes: e.target.value })}
                    placeholder="Notes for interviewers..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Save />}
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const interviewPayload = {
                  candidate: selectedSubmission.candidate._id,
                  taskSubmission: selectedSubmission._id,
                  job: task.mappedJobs?.[0] || null,
                  meetingLink: interviewData.meetingLink,
                  scheduledDate: interviewData.scheduledDate,
                  duration: interviewData.duration,
                  interviewers: interviewData.interviewers.split(',').map(i => i.trim()),
                  interviewType: interviewData.interviewType,
                  notes: interviewData.notes,
                  instructions: interviewData.instructions
                };

                await axios.post(
                  'http://localhost:5000/api/interviews',
                  interviewPayload,
                  {
                    headers: { Authorization: `Bearer ${token}` }
                  }
                );

                setSuccess('Interview scheduled successfully!');
                setScheduleDialog(false);
                fetchInterviews();
                
                // Reset form
                setInterviewData({
                  meetingLink: '',
                  scheduledDate: '',
                  duration: 60,
                  interviewers: '',
                  interviewType: 'technical',
                  notes: '',
                  instructions: ''
                });
              } catch (error) {
                console.error('Error scheduling interview:', error);
                setError('Failed to schedule interview');
              }
            }}
            disabled={!interviewData.meetingLink || !interviewData.scheduledDate || !interviewData.interviewers}
          >
            Schedule Interview
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TaskSubmissions;
